import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  FileSpreadsheet,
  GripVertical,
  Link2,
  Settings2,
} from "lucide-react";
import type { ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import type { TriageStatus } from "@/hooks/use-baseline";
import type { TriageItemRow } from "@/hooks/use-triage";
import { cn } from "@/lib/utils";

interface ReportSheetProps {
  solutionName: string;
  clientName: string;
  reportTitle: string;
  items: TriageItemRow[] | undefined;
  isLoading: boolean;
  /** When provided, the Maps to cell renders the story code as a clickable link. */
  onOpenStory?: (storyId: string) => void;
  /** When provided, an extra trailing "Action" column renders this for each row. */
  rowAction?: (row: TriageItemRow) => ReactNode;
  /** Hide the built-in "Share" button (used when the page itself is the share view). */
  hideShareButton?: boolean;
}

const STATUS_TINTS: Record<TriageStatus, string> = {
  pending: "bg-muted text-foreground/70",
  "in-review": "bg-warning-bg text-warning",
  "in-scope": "bg-success-bg text-success",
  "out-of-scope": "bg-[#FBEAEA] text-[#8B2727] dark:bg-[#2a1414] dark:text-[#f08e8e]",
  deferred: "bg-info-bg text-info",
};

const STATUS_LABEL: Record<TriageStatus, string> = {
  pending: "Pending",
  "in-review": "In Review",
  "in-scope": "In Scope",
  "out-of-scope": "Out of Scope",
  deferred: "Deferred",
};

const REASON_LABEL: Record<string, string> = {
  "change-order": "Change order",
  roadmap: "Roadmap",
  reject: "Reject",
};

const REVIEW_TINTS: Record<string, string> = {
  approved: "bg-success-bg text-success",
  pending: "bg-muted text-foreground/70",
  flagged: "bg-warning-bg text-warning",
  rejected: "bg-[#FBEAEA] text-[#8B2727] dark:bg-[#2a1414] dark:text-[#f08e8e]",
  "not-required": "bg-info-bg/60 text-info",
};

const REVIEW_LABEL: Record<string, string> = {
  approved: "Agreed",
  pending: "Pending",
  flagged: "Needs info",
  rejected: "Rejected",
  "not-required": "Not req.",
};

type ColumnId =
  | "num"
  | "id"
  | "source"
  | "item"
  | "quote"
  | "status"
  | "class"
  | "reason"
  | "mapsTo"
  | "speaker"
  | "p1"
  | "p2"
  | "p3"
  | "notes"
  | "date";

interface RenderCtx {
  index: number;
  onOpenStory: ((storyId: string) => void) | null;
}

interface ColumnDef {
  id: ColumnId;
  label: string;
  width?: string;
  align?: "right";
  /** Sort key. Strings → locale compare, numbers → numeric. */
  sortValue?: (row: TriageItemRow) => string | number | null;
  render: (row: TriageItemRow, ctx: RenderCtx) => ReactNode;
  csvHeader: () => string[];
  csv: (row: TriageItemRow) => string[];
}

const COLUMNS: ColumnDef[] = [
  {
    id: "num",
    label: "#",
    width: "w-10",
    align: "right",
    render: (_, ctx) => <span className="text-muted-foreground tabular-nums">{ctx.index + 1}</span>,
    csvHeader: () => ["#"],
    csv: (_row) => ["row"],
  },
  {
    id: "id",
    label: "ID",
    width: "w-20",
    sortValue: (r) => r.externalKey ?? r.id,
    render: (r) => (
      <span className="font-mono text-[12px] tabular-nums">
        {r.externalKey ?? r.id.slice(0, 6)}
      </span>
    ),
    csvHeader: () => ["ID"],
    csv: (r) => [r.externalKey ?? r.id.slice(0, 6)],
  },
  {
    id: "source",
    label: "Source",
    width: "w-28",
    sortValue: (r) => r.source.externalKey ?? r.source.name,
    render: (r) => (
      <span className="text-muted-foreground tabular-nums truncate" title={r.source.name}>
        {r.source.externalKey ?? r.source.name}
      </span>
    ),
    csvHeader: () => ["Source", "Source name"],
    csv: (r) => [r.source.externalKey ?? "", r.source.name],
  },
  {
    id: "item",
    label: "Item",
    sortValue: (r) => r.extracted,
    render: (r) => (
      <span className="text-foreground/90 truncate max-w-[420px] block" title={r.extracted}>
        {r.extracted}
      </span>
    ),
    csvHeader: () => ["Item"],
    csv: (r) => [r.extracted],
  },
  {
    id: "quote",
    label: "Source quote",
    width: "w-[280px]",
    sortValue: (r) => r.quote,
    render: (r) => (
      <span className="text-foreground/75 italic truncate max-w-[280px] block" title={r.quote}>
        {r.quote ? `"${r.quote}"` : "—"}
      </span>
    ),
    csvHeader: () => ["Source quote"],
    csv: (r) => [r.quote],
  },
  {
    id: "status",
    label: "Status",
    width: "w-28",
    sortValue: (r) => r.effectiveStatus,
    render: (r) => (
      <span
        className={cn(
          "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
          STATUS_TINTS[r.effectiveStatus],
        )}
      >
        {STATUS_LABEL[r.effectiveStatus]}
      </span>
    ),
    csvHeader: () => ["Status"],
    csv: (r) => [STATUS_LABEL[r.effectiveStatus]],
  },
  {
    id: "class",
    label: "Class",
    width: "w-16",
    sortValue: (r) => r.decision?.classification ?? "",
    render: (r) => (
      <span className="text-muted-foreground">{r.decision?.classification ?? "—"}</span>
    ),
    csvHeader: () => ["Class"],
    csv: (r) => [r.decision?.classification ?? ""],
  },
  {
    id: "reason",
    label: "Reason",
    width: "w-28",
    sortValue: (r) => r.decision?.reason ?? "",
    render: (r) => (
      <span className="text-muted-foreground">
        {r.decision?.reason ? (REASON_LABEL[r.decision.reason] ?? r.decision.reason) : "—"}
      </span>
    ),
    csvHeader: () => ["Reason"],
    csv: (r) => [r.decision?.reason ? (REASON_LABEL[r.decision.reason] ?? r.decision.reason) : ""],
  },
  {
    id: "mapsTo",
    label: "Maps to",
    width: "w-44",
    sortValue: (r) => r.mapStory?.code ?? r.mapEpic?.code ?? r.mapDeliverable?.code ?? "",
    render: (r, ctx) => <MapsToCell row={r} onOpenStory={ctx.onOpenStory} />,
    csvHeader: () => ["L1", "L2", "User story"],
    csv: (r) => [r.mapDeliverable?.code ?? "", r.mapEpic?.code ?? "", r.mapStory?.code ?? ""],
  },
  {
    id: "speaker",
    label: "Speaker",
    width: "w-32",
    sortValue: (r) => r.speaker?.name ?? "",
    render: (r) => (
      <span className="text-muted-foreground truncate" title={r.speaker?.name ?? ""}>
        {r.speaker?.name ?? "—"}
      </span>
    ),
    csvHeader: () => ["Speaker", "Speaker org"],
    csv: (r) => [r.speaker?.name ?? "", r.speaker?.org ?? ""],
  },
  {
    id: "p1",
    label: "P1",
    width: "w-20",
    sortValue: (r) => passStatusFor(r, "p1") ?? "",
    render: (r) => <ReviewBadge row={r} pass="p1" />,
    csvHeader: () => ["P1 status", "P1 notes"],
    csv: (r) => csvForPass(r, "p1"),
  },
  {
    id: "p2",
    label: "P2",
    width: "w-20",
    sortValue: (r) => passStatusFor(r, "p2") ?? "",
    render: (r) => <ReviewBadge row={r} pass="p2" />,
    csvHeader: () => ["P2 status", "P2 notes"],
    csv: (r) => csvForPass(r, "p2"),
  },
  {
    id: "p3",
    label: "P3",
    width: "w-20",
    sortValue: (r) => passStatusFor(r, "p3") ?? "",
    render: (r) => <ReviewBadge row={r} pass="p3" />,
    csvHeader: () => ["P3 status", "P3 notes"],
    csv: (r) => csvForPass(r, "p3"),
  },
  {
    id: "notes",
    label: "Notes",
    width: "w-20",
    align: "right",
    sortValue: (r) => r.notes.length,
    render: (r) => (
      <span
        className="text-right text-muted-foreground tabular-nums block"
        title={notesTitle(r) ?? undefined}
      >
        {r.notes.length === 0 ? "—" : r.notes.length}
      </span>
    ),
    csvHeader: () => ["Notes (count)", "Notes"],
    csv: (r) => [
      String(r.notes.length),
      r.notes
        .map((n) => `[${n.authorName ?? "Unknown"} ${formatDate(n.createdAt)}] ${n.text}`)
        .join("\n\n"),
    ],
  },
  {
    id: "date",
    label: "Date",
    width: "w-24",
    sortValue: (r) => new Date(r.sourceDate).getTime(),
    render: (r) => (
      <span className="text-muted-foreground tabular-nums">
        {new Date(r.sourceDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}
      </span>
    ),
    csvHeader: () => ["Date"],
    csv: (r) => [new Date(r.sourceDate).toISOString().slice(0, 10)],
  },
];

const COLUMN_BY_ID = new Map(COLUMNS.map((c) => [c.id, c]));
const DEFAULT_ORDER: ColumnId[] = COLUMNS.map((c) => c.id);

function parseColParam(s: string | undefined): ColumnId[] | null {
  if (!s) return null;
  const ids = s
    .split(",")
    .map((x) => x.trim())
    .filter((x): x is ColumnId => COLUMN_BY_ID.has(x as ColumnId));
  return ids.length ? ids : null;
}

function parseWidthsParam(s: string | undefined): Record<string, number> {
  if (!s) return {};
  const out: Record<string, number> = {};
  for (const pair of s.split(",")) {
    const [id, raw] = pair.split(":").map((x) => x.trim());
    if (!id || !raw) continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 40 && COLUMN_BY_ID.has(id as ColumnId)) {
      out[id] = Math.round(n);
    }
  }
  return out;
}

function serializeWidths(w: Record<string, number>): string {
  const parts = Object.entries(w)
    .filter(([id, n]) => COLUMN_BY_ID.has(id as ColumnId) && Number.isFinite(n))
    .map(([id, n]) => `${id}:${n}`);
  return parts.join(",");
}

export function ReportSheet({
  solutionName,
  clientName,
  reportTitle,
  items,
  isLoading,
  onOpenStory,
  rowAction,
  hideShareButton = false,
}: ReportSheetProps) {
  const sheetName = `${solutionName.replace(/\s+/g, "-").toLowerCase()}-${reportTitle
    .replace(/\s+/g, "-")
    .toLowerCase()}.xlsx`;

  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    cols?: string;
    sort?: string;
    dir?: "asc" | "desc";
    widths?: string;
  };

  const visibleOrder = parseColParam(search.cols) ?? DEFAULT_ORDER;
  const visibleSet = new Set(visibleOrder);
  const sortColId = (search.sort as ColumnId | undefined) ?? null;
  const sortDir = search.dir ?? "asc";
  const persistedWidths = parseWidthsParam(search.widths);

  /** Local widths override during a live drag, then committed to URL on release. */
  const [draftWidths, setDraftWidths] = useState<Record<string, number> | null>(null);
  const widths = draftWidths ?? persistedWidths;

  function commitWidths(next: Record<string, number>) {
    setDraftWidths(null);
    const serialized = serializeWidths(next);
    updateSearch({ widths: serialized || undefined });
  }

  function updateSearch(patch: Partial<typeof search>) {
    navigate({
      to: ".",
      search: (prev: typeof search) => ({ ...prev, ...patch }),
      replace: true,
    });
  }

  function setVisibleOrder(next: ColumnId[]) {
    const same =
      next.length === DEFAULT_ORDER.length && next.every((id, i) => id === DEFAULT_ORDER[i]);
    updateSearch({ cols: same ? undefined : next.join(",") });
  }

  function toggleSort(id: ColumnId) {
    if (sortColId === id) {
      updateSearch({ dir: sortDir === "asc" ? "desc" : "asc" });
    } else {
      updateSearch({ sort: id, dir: "asc" });
    }
  }

  function clearSort() {
    updateSearch({ sort: undefined, dir: undefined });
  }

  function toggleVisible(id: ColumnId) {
    if (visibleSet.has(id)) {
      // Hide: remove from order
      setVisibleOrder(visibleOrder.filter((x) => x !== id));
    } else {
      // Show: re-insert at default position
      const defaultIdx = DEFAULT_ORDER.indexOf(id);
      const next: ColumnId[] = [];
      let inserted = false;
      for (const cur of visibleOrder) {
        const curIdx = DEFAULT_ORDER.indexOf(cur);
        if (!inserted && curIdx > defaultIdx) {
          next.push(id);
          inserted = true;
        }
        next.push(cur);
      }
      if (!inserted) next.push(id);
      setVisibleOrder(next);
    }
  }

  function moveColumn(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= visibleOrder.length) return;
    const next = visibleOrder.slice();
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    setVisibleOrder(next);
  }

  function resetColumns() {
    setDraftWidths(null);
    updateSearch({ cols: undefined, sort: undefined, dir: undefined, widths: undefined });
  }

  const visibleCols = visibleOrder
    .map((id) => COLUMN_BY_ID.get(id))
    .filter((c): c is ColumnDef => Boolean(c));

  const sortedItems = applySort(items ?? [], sortColId, sortDir);

  function exportCsv() {
    if (!items) return;
    const csv = buildCsv(sortedItems, visibleCols);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = sheetName.replace(/\.xlsx$/, ".csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${sortedItems.length} rows`);
  }

  function copyShare() {
    if (typeof window === "undefined") return;
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => toast.success("Share link copied"))
      .catch(() => toast.error("Couldn't copy link"));
  }

  const ctx: RenderCtx = {
    index: 0,
    onOpenStory: onOpenStory ?? null,
  };

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-card overflow-hidden">
      <div className="flex items-center gap-3 bg-success text-white px-4 py-2.5 text-sm">
        <FileSpreadsheet className="h-4 w-4" />
        <span className="font-semibold">
          {clientName} · {solutionName}
        </span>
        <span className="text-white/70 truncate">— {sheetName}</span>
        <span className="ml-auto text-xs text-white/80 tabular-nums">
          {items ? `${items.length} row${items.length === 1 ? "" : "s"}` : "—"}
        </span>
      </div>

      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)] bg-muted/40">
        <ColumnsMenu
          columns={COLUMNS}
          visibleOrder={visibleOrder}
          onToggle={toggleVisible}
          onMove={moveColumn}
          onReset={resetColumns}
        />
        {sortColId && (
          <Button variant="ghost" size="sm" onClick={clearSort}>
            <ArrowUpDown className="h-3.5 w-3.5 mr-1.5" />
            Sorted by {COLUMN_BY_ID.get(sortColId)?.label ?? sortColId} ({sortDir}) — clear
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {!hideShareButton && (
            <Button variant="outline" size="sm" onClick={copyShare}>
              <Link2 className="h-3.5 w-3.5 mr-1.5" />
              Share
            </Button>
          )}
          <Button size="sm" onClick={exportCsv} disabled={!items || items.length === 0}>
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="p-6 space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}

      {items && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold border-b border-[var(--color-border)]">
                {visibleCols.map((col, idx) => (
                  <HeaderCell
                    key={col.id}
                    col={col}
                    idx={idx}
                    sortColId={sortColId}
                    sortDir={sortDir}
                    customWidth={widths[col.id] ?? null}
                    onSort={() => col.sortValue && toggleSort(col.id)}
                    onMove={moveColumn}
                    onResizeStart={(e, startWidth) => {
                      const startX = e.clientX;
                      const baseline = { ...widths };
                      let latest = baseline;
                      const move = (ev: PointerEvent) => {
                        const next = Math.max(40, Math.round(startWidth + (ev.clientX - startX)));
                        latest = { ...baseline, [col.id]: next };
                        setDraftWidths(latest);
                      };
                      const up = () => {
                        document.removeEventListener("pointermove", move);
                        document.removeEventListener("pointerup", up);
                        commitWidths(latest);
                      };
                      document.addEventListener("pointermove", move);
                      document.addEventListener("pointerup", up);
                    }}
                  />
                ))}
                {rowAction && <th className="w-24 px-3 py-2.5" />}
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 && (
                <tr>
                  <td
                    colSpan={visibleCols.length + (rowAction ? 1 : 0)}
                    className="text-center text-sm text-muted-foreground py-12"
                  >
                    No items yet. Add a source on the Intake page to populate this sheet.
                  </td>
                </tr>
              )}
              {sortedItems.map((it, i) => (
                <tr
                  key={it.id}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-muted/40"
                >
                  {visibleCols.map((col) => {
                    const w = widths[col.id];
                    return (
                      <td
                        key={col.id}
                        style={
                          w
                            ? { width: `${w}px`, maxWidth: `${w}px`, minWidth: `${w}px` }
                            : undefined
                        }
                        className={cn(
                          "px-3 py-2 align-middle whitespace-nowrap overflow-hidden",
                          !w && col.width,
                          col.align === "right" && "text-right",
                        )}
                      >
                        {col.render(it, { ...ctx, index: i })}
                      </td>
                    );
                  })}
                  {rowAction && (
                    <td className="px-3 py-2 align-middle whitespace-nowrap text-right">
                      {rowAction(it)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HeaderCell({
  col,
  idx,
  sortColId,
  sortDir,
  customWidth,
  onSort,
  onMove,
  onResizeStart,
}: {
  col: ColumnDef;
  idx: number;
  sortColId: ColumnId | null;
  sortDir: "asc" | "desc";
  customWidth: number | null;
  onSort: () => void;
  onMove: (from: number, to: number) => void;
  onResizeStart: (e: ReactPointerEvent<HTMLElement>, currentWidth: number) => void;
}) {
  const isActive = sortColId === col.id;
  const sortable = Boolean(col.sortValue);

  return (
    <th
      style={
        customWidth
          ? {
              width: `${customWidth}px`,
              maxWidth: `${customWidth}px`,
              minWidth: `${customWidth}px`,
            }
          : undefined
      }
      className={cn(
        "relative text-left px-3 py-2.5 font-semibold whitespace-nowrap select-none",
        !customWidth && col.width,
        col.align === "right" && "text-right",
      )}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(idx));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDrop={(e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer.getData("text/plain"));
        if (Number.isFinite(from)) onMove(from, idx);
      }}
    >
      <button
        type="button"
        onClick={sortable ? onSort : undefined}
        className={cn(
          "inline-flex items-center gap-1 group",
          sortable && "cursor-pointer hover:text-foreground",
          !sortable && "cursor-grab",
        )}
        title={sortable ? "Click to sort. Drag to reorder." : "Drag to reorder."}
      >
        <span>{col.label}</span>
        {sortable && (
          <span
            className={cn(
              "transition-opacity",
              isActive ? "opacity-100" : "opacity-30 group-hover:opacity-60",
            )}
          >
            {isActive && sortDir === "asc" ? (
              <ArrowUp className="h-3 w-3" />
            ) : isActive && sortDir === "desc" ? (
              <ArrowDown className="h-3 w-3" />
            ) : (
              <ArrowUpDown className="h-3 w-3" />
            )}
          </span>
        )}
      </button>
      <span
        title="Drag to resize"
        onPointerDown={(e) => {
          // Stop the th's drag-to-reorder from kicking in
          e.preventDefault();
          e.stopPropagation();
          const th = e.currentTarget.parentElement as HTMLTableCellElement | null;
          const measured = th?.getBoundingClientRect().width ?? 100;
          onResizeStart(e, customWidth ?? measured);
        }}
        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-info/40 active:bg-info/60"
      />
    </th>
  );
}

function ColumnsMenu({
  columns,
  visibleOrder,
  onToggle,
  onMove,
  onReset,
}: {
  columns: ColumnDef[];
  visibleOrder: ColumnId[];
  onToggle: (id: ColumnId) => void;
  onMove: (from: number, to: number) => void;
  onReset: () => void;
}) {
  const visibleSet = new Set(visibleOrder);
  // Visible columns first (in order), then hidden ones
  const ordered: ColumnId[] = [
    ...visibleOrder,
    ...columns.map((c) => c.id).filter((id) => !visibleSet.has(id)),
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings2 className="h-3.5 w-3.5 mr-1.5" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-h-[500px] overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Columns</span>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground font-normal"
            onClick={(e) => {
              e.preventDefault();
              onReset();
            }}
          >
            Reset
          </button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ul className="py-1">
          {ordered.map((id) => {
            const col = columns.find((c) => c.id === id);
            if (!col) return null;
            const visible = visibleSet.has(id);
            const visibleIdx = visibleOrder.indexOf(id);
            const canMoveUp = visible && visibleIdx > 0;
            const canMoveDown = visible && visibleIdx < visibleOrder.length - 1;
            return (
              <li
                key={id}
                className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-muted/60"
              >
                <button
                  type="button"
                  onClick={() => canMoveUp && onMove(visibleIdx, visibleIdx - 1)}
                  disabled={!canMoveUp}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => canMoveDown && onMove(visibleIdx, visibleIdx + 1)}
                  disabled={!canMoveDown}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <label className="flex-1 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={() => onToggle(id)}
                    className="rounded"
                  />
                  <span>{col.label}</span>
                </label>
                <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40" />
              </li>
            );
          })}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MapsToCell({
  row,
  onOpenStory,
}: {
  row: TriageItemRow;
  onOpenStory: ((id: string) => void) | null;
}) {
  const parts: ReactNode[] = [];
  if (row.mapDeliverable) {
    parts.push(
      <span
        key="d"
        className="text-muted-foreground tabular-nums"
        title={`${row.mapDeliverable.code} — ${row.mapDeliverable.title}`}
      >
        {row.mapDeliverable.code}
      </span>,
    );
  }
  if (row.mapEpic) {
    if (parts.length)
      parts.push(
        <span key="s1" className="mx-1 text-muted-foreground/50">
          /
        </span>,
      );
    parts.push(
      <span
        key="e"
        className="text-muted-foreground tabular-nums"
        title={`${row.mapEpic.code} — ${row.mapEpic.title}`}
      >
        {row.mapEpic.code}
      </span>,
    );
  }
  if (row.mapStory) {
    if (parts.length)
      parts.push(
        <span key="s2" className="mx-1 text-muted-foreground/50">
          /
        </span>,
      );
    const story = row.mapStory;
    const titleAttr = `${story.code} — ${story.label?.trim() || story.text}`;
    if (onOpenStory) {
      parts.push(
        <button
          key="us"
          type="button"
          onClick={() => onOpenStory(story.id)}
          className="text-info hover:underline tabular-nums font-medium"
          title={`${titleAttr} (click for full story)`}
        >
          {story.code}
        </button>,
      );
    } else {
      parts.push(
        <span key="us" className="text-muted-foreground tabular-nums" title={titleAttr}>
          {story.code}
        </span>,
      );
    }
  }
  return parts.length > 0 ? (
    <span className="inline-flex items-center">{parts}</span>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

function ReviewBadge({ row, pass }: { row: TriageItemRow; pass: "p1" | "p2" | "p3" }) {
  const r = row.reviewerStatuses.find((s) => s.pass === pass);
  if (!r) return <span className="text-muted-foreground/60">—</span>;
  const tint = REVIEW_TINTS[r.status] ?? "bg-muted text-foreground/70";
  const label = REVIEW_LABEL[r.status] ?? r.status;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
        tint,
      )}
      title={r.note ?? undefined}
    >
      {label}
    </span>
  );
}

function passStatusFor(row: TriageItemRow, pass: "p1" | "p2" | "p3"): string | null {
  return row.reviewerStatuses.find((s) => s.pass === pass)?.status ?? null;
}

function csvForPass(row: TriageItemRow, pass: "p1" | "p2" | "p3"): string[] {
  const r = row.reviewerStatuses.find((s) => s.pass === pass);
  if (!r) return ["", ""];
  return [REVIEW_LABEL[r.status] ?? r.status, r.note ?? ""];
}

function notesTitle(it: TriageItemRow): string | undefined {
  if (it.notes.length === 0) return undefined;
  return it.notes
    .map((n) => `${n.authorName ?? "Unknown"} — ${formatDate(n.createdAt)}\n${n.text}`)
    .join("\n\n");
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function applySort(
  items: TriageItemRow[],
  sortId: ColumnId | null,
  dir: "asc" | "desc",
): TriageItemRow[] {
  if (!sortId) return items;
  const col = COLUMN_BY_ID.get(sortId);
  if (!col?.sortValue) return items;
  const sorter = col.sortValue;
  const sign = dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = sorter(a);
    const vb = sorter(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1; // nulls last
    if (vb == null) return -1;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va).localeCompare(String(vb)) * sign;
  });
}

function buildCsv(items: TriageItemRow[], visibleCols: ColumnDef[]): string {
  const header = visibleCols.flatMap((c) => c.csvHeader());
  const rows = items.map((r) => visibleCols.flatMap((c) => c.csv(r)));
  return [header, ...rows].map((row) => row.map(escapeCsvCell).join(",")).join("\n");
}

function escapeCsvCell(cell: string): string {
  if (cell == null) return "";
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}
